import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import { DOCUMENT } from '../../constants/documentTheme';
import DeveloperAccessCallout from '../../components/DeveloperAccessCallout';

const PARTNER_AUTH = { usePartnerAuth: true };

const ACCESS_OPTIONS = ['packages', 'read', 'write', 'all'];

export function visibleApiKeys(keys) {
  if (!Array.isArray(keys)) return [];
  return keys.filter((key) => !key?.revoked && !key?.revoked_at);
}

const DeveloperKeys = () => {
  const { t } = useTranslation();
  const [keys, setKeys] = useState([]);
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [environment, setEnvironment] = useState('live');
  const [access, setAccess] = useState('packages');
  const [newSecret, setNewSecret] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const accessLabel = useCallback((value) => {
    const key = ACCESS_OPTIONS.includes(value) || value === 'custom' ? value : 'custom';
    const normalized = key === 'packages' ? 'Packages' : `${key.charAt(0).toUpperCase()}${key.slice(1)}`;
    return t(`developerPortal.keys.access${normalized}`);
  }, [t]);

  const loadEntitlements = useCallback(async () => {
    try {
      const planRes = await api.get('/partners/plan', PARTNER_AUTH);
      setEntitlements(planRes.data?.entitlements || null);
    } catch {
      // ignore
    }
  }, []);

  const loadKeys = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get('/partners/developer/keys', PARTNER_AUTH);
      setKeys(visibleApiKeys(res.data?.keys || res.data?.data || res.data || []));
      if (res.data?.entitlements) {
        setEntitlements((prev) => ({
          ...(prev || {}),
          features: res.data.entitlements.features || prev?.features,
          api: res.data.entitlements.api || prev?.api,
        }));
      }
    } catch (err) {
      const code = err.response?.data?.error;
      const message = err.response?.data?.message || err.response?.data?.error || t('developerPortal.errors.loadFailed');
      if (code === 'partner_feature_limit' || err.response?.status === 403) {
        setError(typeof message === 'string' ? message : t('developerPortal.home.noApi'));
        if (err.response?.data?.entitlements) {
          setEntitlements(err.response.data.entitlements);
        }
      } else {
        setError(typeof message === 'string' ? message : t('developerPortal.errors.loadFailed'));
      }
      setKeys([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  const refresh = useCallback(async () => {
    await loadEntitlements();
    await loadKeys();
  }, [loadEntitlements, loadKeys]);

  useEffect(() => {
    loadEntitlements();
    loadKeys();
  }, [loadEntitlements, loadKeys]);

  const resetCreateForm = () => {
    setName('');
    const hasProduction = Boolean(entitlements?.features?.hasProductionApi);
    const hasSandbox = Boolean(entitlements?.features?.hasApiSandbox);
    if (hasSandbox) {
      setEnvironment('test');
      setAccess(hasProduction ? 'all' : 'packages');
    } else {
      setEnvironment('live');
      setAccess('packages');
    }
  };

  const handleCreate = async () => {
    try {
      setCreating(true);
      setError('');
      const res = await api.post('/partners/developer/keys', {
        name,
        environment,
        access,
      }, PARTNER_AUTH);
      const created = res.data?.key;
      setNewSecret(typeof created === 'string' ? created : (created?.key || res.data?.secret || null));
      resetCreateForm();
      setDialogOpen(false);
      await loadKeys();
    } catch (err) {
      const code = err.response?.data?.error;
      const message = err.response?.data?.message || err.response?.data?.error || t('developerPortal.errors.createFailed');
      if (code === 'partner_feature_limit' || err.response?.status === 403) {
        if (err.response?.data?.entitlements) {
          setEntitlements(err.response.data.entitlements);
        }
      }
      setError(typeof message === 'string' ? message : t('developerPortal.errors.createFailed'));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('developerPortal.keys.confirmDelete'))) return;
    try {
      await api.delete(`/partners/developer/keys/${id}`, PARTNER_AUTH);
    } catch (err) {
      if (err.response?.status !== 404) {
        setError(err.response?.data?.error || t('developerPortal.errors.deleteFailed'));
        return;
      }
    }
    await loadKeys();
  };

  const features = entitlements?.features || {};
  const hasSandbox = Boolean(features.hasApiSandbox);
  const hasProduction = Boolean(features.hasProductionApi);
  const canUseApi = true; // Package create/upload keys are free on every plan
  const accessOptions = (!hasProduction && environment === 'live')
    ? ['packages']
    : ACCESS_OPTIONS;
  const accessHintKey = ACCESS_OPTIONS.includes(access) || access === 'custom'
    ? `developerPortal.keys.accessHint${access === 'packages' ? 'Packages' : `${access.charAt(0).toUpperCase()}${access.slice(1)}`}`
    : 'developerPortal.keys.accessHintCustom';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box>
          <Typography component="h1" sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 28, md: 34 }, mb: 0.75 }}>
            {t('developerPortal.keys.title')}
          </Typography>
          <Typography sx={{ color: DOCUMENT.ink2, maxWidth: '58ch' }}>
            {t('developerPortal.keys.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          disableElevation
          onClick={() => {
            resetCreateForm();
            setDialogOpen(true);
          }}
          disabled={!canUseApi}
          sx={{ bgcolor: DOCUMENT.oxblood, borderRadius: 0, textTransform: 'none', '&:hover': { bgcolor: DOCUMENT.oxblood } }}
        >
          {t('developerPortal.keys.create')}
        </Button>
      </Box>

      {(!hasSandbox || !hasProduction) ? (
        <DeveloperAccessCallout
          entitlements={entitlements}
          onEntitlementsChange={refresh}
          errorMessage={error || undefined}
        />
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      ) : null}

      {newSecret ? (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setNewSecret(null)}>
          {t('developerPortal.keys.secretOnce', { secret: newSecret })}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: DOCUMENT.oxblood }} />
        </Box>
      ) : keys.length === 0 ? (
        <Typography sx={{ color: DOCUMENT.ink3 }}>{t('developerPortal.keys.empty')}</Typography>
      ) : (
        <Table size="small" sx={{ bgcolor: DOCUMENT.surface, border: `1px solid ${DOCUMENT.ruleHard}` }}>
          <TableHead>
            <TableRow>
              <TableCell>{t('developerPortal.keys.colName')}</TableCell>
              <TableCell>{t('developerPortal.keys.colEnv')}</TableCell>
              <TableCell>{t('developerPortal.keys.colAccess')}</TableCell>
              <TableCell>{t('developerPortal.keys.colPrefix')}</TableCell>
              <TableCell>{t('developerPortal.keys.colCreated')}</TableCell>
              <TableCell align="right">{t('developerPortal.keys.colActions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {keys.map((key) => (
              <TableRow key={key.id}>
                <TableCell>{key.name || '—'}</TableCell>
                <TableCell>{key.environment || '—'}</TableCell>
                <TableCell>{accessLabel(key.access)}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{key.prefix || key.key_prefix || '—'}</TableCell>
                <TableCell>{key.created_at || key.createdAt ? new Date(key.created_at || key.createdAt).toLocaleDateString() : '—'}</TableCell>
                <TableCell align="right">
                  <Button size="small" color="error" onClick={() => handleDelete(key.id)} sx={{ textTransform: 'none' }}>
                    {t('developerPortal.keys.revoke')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => {
          if (!creating) {
            setDialogOpen(false);
            resetCreateForm();
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t('developerPortal.keys.createTitle')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label={t('developerPortal.keys.nameLabel')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
          />
          <FormControl fullWidth>
            <InputLabel id="key-env-label">{t('developerPortal.keys.envLabel')}</InputLabel>
            <Select
              labelId="key-env-label"
              label={t('developerPortal.keys.envLabel')}
              value={environment}
              onChange={(e) => {
                const nextEnv = e.target.value;
                setEnvironment(nextEnv);
                if (nextEnv === 'live' && !hasProduction) {
                  setAccess('packages');
                }
                if (nextEnv === 'test' && !hasSandbox) {
                  setEnvironment('live');
                  setAccess('packages');
                }
              }}
            >
              <MenuItem value="test" disabled={!hasSandbox}>
                {t('developerPortal.keys.envTest')}
              </MenuItem>
              <MenuItem value="live">
                {t('developerPortal.keys.envLive')}
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="key-access-label">{t('developerPortal.keys.accessLabel')}</InputLabel>
            <Select
              labelId="key-access-label"
              label={t('developerPortal.keys.accessLabel')}
              value={accessOptions.includes(access) ? access : 'packages'}
              onChange={(e) => setAccess(e.target.value)}
            >
              {accessOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {accessLabel(option)}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{t(accessHintKey)}</FormHelperText>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialogOpen(false);
              resetCreateForm();
            }}
            disabled={creating}
            sx={{ textTransform: 'none' }}
          >
            {t('developerPortal.common.cancel')}
          </Button>
          <Button
            onClick={handleCreate}
            disabled={creating || !name.trim()}
            variant="contained"
            sx={{ textTransform: 'none', bgcolor: DOCUMENT.oxblood }}
          >
            {creating ? <CircularProgress size={18} color="inherit" /> : t('developerPortal.keys.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeveloperKeys;
